import debounce from 'debounce'
import { memo, useEffect, useRef } from 'react'
import { useAsync } from 'react-use'
import styled from 'styled-components'
import { AppError, errors } from '~/prix'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import { calculateGooglePovBetweenPositions } from '~/prix/utils/googlePov'
import WebGLContextManager from './utils/webGLContextManager'

const Wrapper = styled.div``
const StreetView = styled.div``
const Message = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: rgba(255, 255, 255, 1);
  z-index: 1;
`

export interface StreetViewChange {
  position: google.maps.LatLngLiteral
  pov: google.maps.StreetViewPov
  google: typeof google
}
export interface StreetViewPanoramaProps {
  position: google.maps.LatLngLiteral
  markerPosition?: google.maps.LatLngLiteral
  onChange?: (change: StreetViewChange) => void
  debounceChange?: number
  hideWhenNotAvailable?: boolean
  radius?: number
  width?: string
  height?: string
}

const IGNORE_EVENTS_TIMEOUT = 1000 // milliseconds

function StreetViewPanorama({
  position,
  markerPosition = position,
  onChange,
  debounceChange = 250,
  hideWhenNotAvailable = false,
  radius = 80,
  width = '100%',
  height = '100%',
}: StreetViewPanoramaProps) {
  const streetViewElementRef = useRef<HTMLDivElement>(null)
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)
  const markerTimeoutRef = useRef<number | null>(null)
  const lastProgrammaticChangeTime = useRef<number>(0)
  const { google, isLoading: isLoadingGoogleMaps } = useGoogleMaps()

  // Obter a posição real possível do StreetView e calcular o POV
  const streetViewResult = useAsync(async () => {
    if (!google) {
      return null
    }

    const service = new google.maps.StreetViewService()
    const result = await service
      .getPanorama({
        location: position,
        preference: google.maps.StreetViewPreference.NEAREST,
        radius,
        source: google.maps.StreetViewSource.OUTDOOR,
      })
      .catch(error => {
        if (error.code === google.maps.StreetViewStatus.ZERO_RESULTS) {
          throw errors.notFound('Sem imagens')
        }
        throw errors.unknown('Erro ao buscar o StreetView', undefined, error)
      })

    if (!result.data.location?.latLng) {
      throw errors.notFound('Sem imagens')
    }

    const foundLocation = result.data.location.latLng

    const pov = calculateGooglePovBetweenPositions({
      origin: foundLocation,
      target: new google.maps.LatLng(position),
      google,
    })

    return {
      position: foundLocation,
      pov,
    }
  }, [google, position.lat, position.lng])

  const error = streetViewResult.error as AppError | null
  const isLoading = streetViewResult.loading || isLoadingGoogleMaps

  // Inicializar e atualizar o StreetView de acordo com a posição calculada
  useEffect(() => {
    if (!streetViewElementRef.current || !google || isLoading || !streetViewResult.value) {
      return
    }

    let panorama
    if (!panoramaRef.current) {
      panorama = new google.maps.StreetViewPanorama(streetViewElementRef.current, {
        position: streetViewResult.value.position,
        pov: streetViewResult.value.pov,
        visible: true,
        addressControl: false,
        linksControl: false,
        panControl: false,
        zoomControl: false,
      })
      panoramaRef.current = panorama

      const marker = new google.maps.Marker({
        position,
        map: panoramaRef.current,
        visible: true,
      })
      markerRef.current = marker
    } else {
      panorama = panoramaRef.current
      panorama.setPosition(streetViewResult.value.position)
      panorama.setPov(streetViewResult.value.pov)
      panorama.setZoom(0)
    }

    lastProgrammaticChangeTime.current = Date.now()

    google.maps.event.addListener(panorama, 'status_changed', () => {
      if (panorama.getStatus() === google.maps.StreetViewStatus.OK) {
        //adiciona canvas ao webgl context manager
        const canvas = streetViewElementRef.current?.querySelector('canvas')
        if (canvas) {
          WebGLContextManager.getInstance(3).createContext(canvas)
        }
      }
    })

    return () => {
      const div = streetViewElementRef.current
      if (div) {
        while (div.firstChild) {
          div.removeChild(div.firstChild)
        }
      }

      // Remove o panorama
      if (panoramaRef.current) {
        panoramaRef.current.setVisible(false)
        panoramaRef.current = null
      }
    }
  }, [streetViewResult.value, google, isLoading, streetViewElementRef.current])

  // Atualizar a posição do marcador de acordo com a posição
  useEffect(() => {
    const marker = markerRef.current
    if (!marker) {
      return
    }

    if (markerTimeoutRef.current) {
      window.clearTimeout(markerTimeoutRef.current)
    }

    marker.setVisible(false)
    marker.setPosition(markerPosition)

    const timeout = window.setTimeout(() => {
      marker.setVisible(true)
    }, 1000)
    markerTimeoutRef.current = timeout

    return () => {
      // Limpa o timeout do marcador
      if (markerTimeoutRef.current) {
        window.clearTimeout(markerTimeoutRef.current)
        markerTimeoutRef.current = null
      }

      // Remover o marcador
      if (markerRef.current) {
        markerRef.current.setMap(null)
        markerRef.current = null
      }
    }
  }, [markerPosition])

  // Registrar mudança externa
  useEffect(() => {
    lastProgrammaticChangeTime.current = Date.now()
  }, [position])

  // Acompanhar eventos de mudança de posição e POV
  useEffect(() => {
    const panorama = panoramaRef.current
    if (!panorama || !onChange || !google) {
      return
    }

    const debouncedOnChange = debounce(onChange, debounceChange)

    const onPositionChanged = () => {
      const now = Date.now()
      if (now - lastProgrammaticChangeTime.current < IGNORE_EVENTS_TIMEOUT) {
        return
      }

      const position = panorama.getPosition()?.toJSON()
      const pov = panorama.getPov()

      if (!position) {
        return
      }

      debouncedOnChange({ position, pov, google })
    }

    const onPovChanged = () => {
      const now = Date.now()
      if (now - lastProgrammaticChangeTime.current < IGNORE_EVENTS_TIMEOUT) {
        return
      }

      const position = panorama.getPosition()?.toJSON()
      const pov = panorama.getPov()

      if (!position) {
        return
      }

      debouncedOnChange({ position, pov, google })
    }

    const positionChangedListener = panorama.addListener('position_changed', onPositionChanged)
    const povChangedListener = panorama.addListener('pov_changed', onPovChanged)

    return () => {
      positionChangedListener.remove()
      povChangedListener.remove()
      google.maps.event.clearInstanceListeners(panorama)
    }
  }, [panoramaRef.current, google, onChange, debounceChange])

  const display = hideWhenNotAvailable && error && error.key === 'notFound' ? 'none' : undefined
  const style = { width, height }

  return (
    <Wrapper style={{ ...style, display }}>
      <StreetView ref={streetViewElementRef} style={style} />
      {error && !isLoading && (error.key !== 'notFound' || !hideWhenNotAvailable) ? (
        <Message style={style}>{error.message}</Message>
      ) : null}
      {isLoading && !hideWhenNotAvailable ? <Message style={style}>Carregando...</Message> : null}
    </Wrapper>
  )
}
export default memo(StreetViewPanorama)
